import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import { ApiService } from './api.service';
import { PlanService } from './plan.service';

export interface QueuedMilkEntry {
  tempId: string;
  payload: any;
  queuedAt: number;
}

/**
 * Offline-first helper for Milkomatic CRM.
 * - Tracks online/offline state:
 *     • Native Android/iOS: uses @capacitor/network (fires reliably in WebView)
 *     • Web browser: falls back to navigator.onLine + window events
 * - Caches members + dairy-accounts in localStorage
 * - Queues milk-collection entries when offline; drains on reconnect
 */
@Injectable({ providedIn: 'root' })
export class OfflineService {

  private readonly MEMBERS_KEY  = 'milko_cache_members';
  private readonly ACCOUNTS_KEY = 'milko_cache_dairy_accounts';
  private readonly MC_QUEUE_KEY = 'milko_mc_offline_queue';

  /** Emits true when online, false when offline. */
  readonly isOnline$ = new BehaviorSubject<boolean>(true);

  constructor(private api: ApiService, private plan: PlanService, private ngZone: NgZone) {
    this.initNetworkDetection();
  }

  /** True when the user's plan is BASIC, ADVANCE or PREMIUM. */
  get isOfflineSyncAllowed(): boolean { return this.plan.isBasic || this.plan.isAdvance; }

  // ── Silent background preload ──────────────────────────────────────────────
  // Called once after login and again each time connection is restored.
  // Downloads members, dairy accounts, all rate lists, and all milk rates so
  // the app works fully offline without the user needing to do anything.

  preloadAllOfflineData(dId: number): void {
    if (!this.isOnline || !dId || !this.isOfflineSyncAllowed) return;

    // 1. Members
    this.api.post<any>('members_and_accounts/get_all_members_and_accounts.php',
      { d_id: dId, g_id: 6, search_type: 'members' })
      .subscribe({
        next: (res: any) => {
          if (res?.success) this.cacheMembers(dId, res.members_and_accounts || []);
        },
        error: () => {}
      });

    // 2. Dairy accounts
    this.api.post<any>('members_and_accounts/get_all_members_and_accounts.php',
      { d_id: dId, search_type: 'accounts' })
      .subscribe({
        next: (res: any) => {
          if (res?.members_and_accounts) this.cacheDairyAccounts(dId, res.members_and_accounts || []);
        },
        error: () => {}
      });

    // 3. All rate lists → then fetch milk rates for each list
    this.api.post<any>('rate_list/list_all_rate_list.php', { d_id: dId })
      .subscribe({
        next: (res: any) => {
          const lists: any[] = res?.rateLists || res?.rate_lists || [];
          lists.forEach((list: any) => {
            if (!list?.id) return;
            this.api.post<any>('rate_list/get_milk_rates.php', { d_id: dId, list_id: list.id })
              .subscribe({
                next: (rRes: any) => {
                  const rates = rRes?.milkrates || rRes?.milkRates || [];
                  if (rates.length) this.cacheRateList(dId, list.id, rates);
                },
                error: () => {}
              });
          });
        },
        error: () => {}
      });
  }

  private async initNetworkDetection(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      // Native: use Capacitor Network plugin — reliable in Android WebView
      try {
        const status = await Network.getStatus();
        this.isOnline$.next(status.connected);
      } catch { /* ignore — default to online */ }

      Network.addListener('networkStatusChange', status => {
        this.ngZone.run(() => this.isOnline$.next(status.connected));
      });
    } else {
      // Web browser fallback.
      // NOTE: Chrome DevTools "Network > Offline" throttling does NOT fire
      // window.online/offline events and does NOT change navigator.onLine.
      // Only a real network disconnect (or Application→ServiceWorkers→Offline)
      // properly triggers these events.  Use all available signals:

      this.isOnline$.next(navigator.onLine);

      // 1. Standard online/offline events (real network changes)
      window.addEventListener('online',  () => this.ngZone.run(() => this.isOnline$.next(true)));
      window.addEventListener('offline', () => this.ngZone.run(() => this.isOnline$.next(false)));

      // 2. Re-check when the page becomes visible (app resume / tab switch).
      //    Critical: the 'online' event can fire while the page is hidden and be missed.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.ngZone.run(() => this.isOnline$.next(navigator.onLine));
        }
      });

      // 3. Re-check when the window gets focus (user clicks back into the tab).
      window.addEventListener('focus', () => this.ngZone.run(() => this.isOnline$.next(navigator.onLine)));
    }
  }

  get isOnline(): boolean { return this.isOnline$.getValue(); }

  // ── Members cache ──────────────────────────────────────────────────────────

  cacheMembers(dId: number, members: any[]): void {
    try {
      localStorage.setItem(
        `${this.MEMBERS_KEY}_${dId}`,
        JSON.stringify({ ts: Date.now(), data: members })
      );
    } catch { /* localStorage full */ }
  }

  getCachedMembers(dId: number): any[] | null {
    try {
      const raw = localStorage.getItem(`${this.MEMBERS_KEY}_${dId}`);
      return raw ? (JSON.parse(raw).data ?? null) : null;
    } catch { return null; }
  }

  // ── Dairy accounts cache ───────────────────────────────────────────────────

  cacheDairyAccounts(dId: number, accounts: any[]): void {
    try {
      localStorage.setItem(
        `${this.ACCOUNTS_KEY}_${dId}`,
        JSON.stringify({ ts: Date.now(), data: accounts })
      );
    } catch { /* localStorage full */ }
  }

  getCachedDairyAccounts(dId: number): any[] | null {
    try {
      const raw = localStorage.getItem(`${this.ACCOUNTS_KEY}_${dId}`);
      return raw ? (JSON.parse(raw).data ?? null) : null;
    } catch { return null; }
  }

  // ── Rate list cache ────────────────────────────────────────────────────────
  // Stores all milk rates for each rate-list ID so getRate() works offline.
  // Key: milko_cache_rates_<dId>_<listId>  Value: { ts, data: MilkRate[] }

  private readonly RATES_KEY = 'milko_cache_rates';

  cacheRateList(dId: number, listId: number, rates: any[]): void {
    try {
      localStorage.setItem(
        `${this.RATES_KEY}_${dId}_${listId}`,
        JSON.stringify({ ts: Date.now(), data: rates })
      );
    } catch { /* localStorage full */ }
  }

  /**
   * Looks up a milk rate from the local cache using the same logic as
   * the server endpoint: exact match on fat_per + snf_per for a given list.
   * Returns the first matching rate object, or null if not cached / not found.
   */
  lookupCachedRate(
    dId: number,
    listId: number,
    fatPer: number,
    basisPer: number,
    searchType: 'fat_snf' | 'fat_clr' = 'fat_snf'
  ): any | null {
    try {
      const raw = localStorage.getItem(`${this.RATES_KEY}_${dId}_${listId}`);
      if (!raw) return null;
      const rates: any[] = JSON.parse(raw).data ?? [];
      const rateBasis = (r: any): number => Number(searchType === 'fat_clr' ? (r.clr ?? r.clr_per) : r.snf_per);
      // Exact match first
      let match = rates.find(
        r => Number(r.fat_per) === fatPer && rateBasis(r) === basisPer
      );
      // Nearest match: smallest combined delta (mirrors server fuzzy lookup)
      if (!match && rates.length) {
        match = rates.reduce((best, r) => {
          const d  = Math.abs(Number(r.fat_per) - fatPer) + Math.abs(rateBasis(r) - basisPer);
          const db = Math.abs(Number(best.fat_per) - fatPer) + Math.abs(rateBasis(best) - basisPer);
          return d < db ? r : best;
        });
      }
      return match ?? null;
    } catch { return null; }
  }

  // ── Offline milk-entry queue ───────────────────────────────────────────────

  /** Saves a payload to the offline queue and returns its temporary ID. */
  /** Returns the tempId on success, or null if the plan doesn't allow offline sync. */
  queueMilkEntry(payload: any): string | null {
    if (!this.isOfflineSyncAllowed) return null;
    const tempId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const queue  = this.getQueue();
    queue.push({ tempId, payload, queuedAt: Date.now() });
    this._saveQueue(queue);
    return tempId;
  }

  getQueue(): QueuedMilkEntry[] {
    try {
      const raw = localStorage.getItem(this.MC_QUEUE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }

  removeFromQueue(tempId: string): void {
    this._saveQueue(this.getQueue().filter(e => e.tempId !== tempId));
  }

  getQueueCount(): number { return this.getQueue().length; }

  private _saveQueue(queue: QueuedMilkEntry[]): void {
    try { localStorage.setItem(this.MC_QUEUE_KEY, JSON.stringify(queue)); } catch { /* ignore */ }
  }
}
